import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ArrowLeft, Save, Send, Eye, Receipt, CheckCircle2, Copy } from "lucide-react";
import { PaymentScheduleEditor } from "@/components/contracts/PaymentScheduleEditor";
import { ListEditor } from "@/components/contracts/ContractDefaultsForm";
import { CONTRACT_STATUS } from "@/pages/ContractsPage";
import { ChangeOrdersCard } from "@/components/contracts/ChangeOrdersCard";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function ContractEditorPage() {
  const { contractId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [send, setSend] = useState({ send_email: true, send_sms: true, message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(() => axios.get(`${API}/contracts/${contractId}`).then(r => setC(r.data)).catch(() => toast.error("Contract not found")), [contractId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setC(p => ({ ...p, [k]: v }));
  const locked = c?.status === "signed";

  const save = async () => {
    setSaving(true);
    try {
      const { invoice, ...payload } = c;
      const r = await axios.put(`${API}/contracts/${contractId}`, payload);
      setC(r.data);
      toast[r.data.pending_approval ? "info" : "success"](r.data.pending_approval ? "Saved — financial changes sent for owner approval" : "Contract saved");
    } catch (e) {
      if (e.response?.status === 202) toast.info("Changes sent to owner for approval");
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const { invoice, ...payload } = c;
      await axios.put(`${API}/contracts/${contractId}`, payload).catch(e => { if (e.response?.status !== 202) throw e; });
      const r = await axios.post(`${API}/contracts/${contractId}/send`, send);
      const parts = [send.send_email && (r.data.email_sent ? "email sent" : "email failed"), send.send_sms && (r.data.sms_sent ? "text sent" : "text failed")].filter(Boolean);
      toast[parts.some(p => p.includes("failed")) ? "warning" : "success"](`Package ${parts.join(", ")}`);
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!c) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  const signUrl = `${window.location.origin}/sign/${c.sign_token}`;

  return (
    <div className="space-y-5 animate-fade-in pb-24" data-testid="contract-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-testid="contract-title">Contract {c.contract_number} <Badge className={CONTRACT_STATUS[c.status]}>{c.status}</Badge></h1>
            <p className="text-xs text-slate-500">{c.owner_name} · {money(c.total)}{c.invoice && <> · <button className="underline" onClick={() => navigate(`/invoices/${c.invoice.invoice_id}`)} data-testid="open-invoice-link">Invoice {c.invoice.invoice_number}</button></>}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild><a href={`${API}/contracts/${contractId}/pdf`} target="_blank" rel="noreferrer" data-testid="preview-pdf-btn"><Eye className="w-4 h-4 mr-1" /> Preview PDF</a></Button>
          {c.invoice && <Button variant="outline" size="sm" asChild><a href={`${API}/invoices/${c.invoice.invoice_id}/pdf`} target="_blank" rel="noreferrer" data-testid="preview-invoice-btn"><Receipt className="w-4 h-4 mr-1" /> Invoice PDF</a></Button>}
          {!locked && <Button size="sm" onClick={save} disabled={saving} data-testid="save-contract-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>}
          {!locked && <Button size="sm" className="bg-indigo-600" onClick={() => setSendOpen(true)} data-testid="send-package-btn"><Send className="w-4 h-4 mr-1" /> {c.sent_at ? "Resend Package" : "Send Package"}</Button>}
        </div>
      </div>

      {locked && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="signed-banner">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
          <div className="text-sm"><p className="font-medium text-green-800">Signed by {c.signed_by} on {new Date(c.signed_at).toLocaleString()}</p><p className="text-green-700 text-xs">IP {c.signed_ip}. The signed PDF and invoice are saved under the project's documents. This contract is now read-only.</p></div>
        </div>
      )}
      {!locked && c.sent_at && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex flex-wrap items-center gap-2" data-testid="sent-banner">
          Sent {new Date(c.sent_at).toLocaleString()}{c.viewed_at && ` · viewed ${new Date(c.viewed_at).toLocaleString()}`} · Signing link:
          <code className="bg-white px-1 rounded truncate max-w-[260px]">{signUrl}</code>
          <button onClick={() => { navigator.clipboard.writeText(signUrl); toast.success("Link copied"); }} className="underline flex items-center gap-1" data-testid="copy-sign-link"><Copy className="w-3 h-3" /> copy</button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2"><CardHeader><CardTitle className="text-base">Parties &amp; property</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3">
            <F label="Agreement date" type="date" value={c.agreement_date?.slice(0, 10) || ""} onChange={v => set("agreement_date", v)} disabled={locked} testId="c-agreement-date" />
            <F label="Owner(s) — as they should appear" value={c.owner_name} onChange={v => set("owner_name", v)} disabled={locked} testId="c-owner-name" />
            <F label="Owner email" value={c.owner_email || ""} onChange={v => set("owner_email", v)} disabled={locked} testId="c-owner-email" />
            <F label="Owner phone" value={c.owner_phone || ""} onChange={v => set("owner_phone", v)} disabled={locked} testId="c-owner-phone" />
            <div className="sm:col-span-2"><F label='Property address ("the land")' value={c.property_address} onChange={v => set("property_address", v)} disabled={locked} testId="c-address" /></div>
            <F label="Commence work by" type="date" value={c.start_date?.slice(0, 10) || ""} onChange={v => set("start_date", v)} disabled={locked} testId="c-start" />
            <F label="Completed by" type="date" value={c.completion_date?.slice(0, 10) || ""} onChange={v => set("completion_date", v)} disabled={locked} testId="c-complete" />
            <div className="sm:col-span-2"><F label="Work order reference (Schedule A)" value={c.work_order_ref || ""} onChange={v => set("work_order_ref", v)} disabled={locked} testId="c-work-order" /></div>
          </CardContent></Card>

        <Card><CardHeader><CardTitle className="text-base">Company header</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <F label="Company" value={c.company_name} onChange={v => set("company_name", v)} disabled={locked} testId="c-company" />
            <div><Label className="text-xs">Address lines</Label><Textarea rows={2} value={(c.company_address_lines || []).join("\n")} onChange={e => set("company_address_lines", e.target.value.split("\n"))} disabled={locked} /></div>
            <F label="Phone" value={c.company_phone || ""} onChange={v => set("company_phone", v)} disabled={locked} testId="c-phone" />
          </CardContent></Card>
      </div>

      <Card><CardHeader><CardTitle className="text-base">II. Payment schedule — total {money(c.total)}</CardTitle></CardHeader>
        <CardContent><PaymentScheduleEditor schedule={c.payment_schedule || []} total={c.total} onChange={v => set("payment_schedule", v)} readOnly={locked} /></CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">Clauses</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label className="text-xs">I.</Label><Textarea rows={4} value={c.clause_i} onChange={e => set("clause_i", e.target.value)} disabled={locked} data-testid="c-clause-i" /></div>
          {(c.clauses || []).map((cl, i) => (
            <div key={i} className="space-y-1">
              <div className="flex gap-2 items-center"><span className="text-sm text-slate-500 w-8">{["III", "IV", "V", "VI", "VII", "VIII", "IX", "X"][i]}.</span><Input value={cl.title} onChange={e => set("clauses", c.clauses.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} disabled={locked} data-testid={`c-clause-title-${i}`} /></div>
              <Textarea rows={3} value={cl.body} onChange={e => set("clauses", c.clauses.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} disabled={locked} data-testid={`c-clause-body-${i}`} />
            </div>
          ))}
        </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-5">
        <ListEditor title='Schedule "A" — Contractor responsibilities' items={c.schedule_a || []} onChange={v => set("schedule_a", v)} testId="c-sched-a" />
        <ListEditor title='Schedule "B" — Home owner responsibilities' items={c.schedule_b || []} onChange={v => set("schedule_b", v)} testId="c-sched-b" />
      </div>

      {locked && <ChangeOrdersCard contract={c} isOwner={user?.role === "owner"} onChanged={load} />}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-package-dialog">
          <DialogHeader><DialogTitle>Send agreement package</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">The customer receives a secure link to review and e-sign. The contract PDF{c.invoice ? " and invoice PDF are" : " is"} attached to the email.</p>
          <div className="space-y-3">
            <Row label={`Email → ${c.owner_email || "no email on file"}`} checked={send.send_email && !!c.owner_email} disabled={!c.owner_email} onChange={v => setSend({ ...send, send_email: v })} testId="send-email-toggle" />
            <Row label={`Text message → ${c.owner_phone || "no phone on file"}`} checked={send.send_sms && !!c.owner_phone} disabled={!c.owner_phone} onChange={v => setSend({ ...send, send_sms: v })} testId="send-sms-toggle" />
            <div><Label className="text-xs">Personal note (optional, email only)</Label><Textarea rows={3} value={send.message} onChange={e => setSend({ ...send, message: e.target.value })} placeholder="Hi Allison & Herman, here is the agreement we discussed…" data-testid="send-message" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={doSend} disabled={sending || (!(send.send_email && c.owner_email) && !(send.send_sms && c.owner_phone))} data-testid="send-package-confirm">{sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send for signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, value, onChange, type = "text", disabled, testId }) => (
  <div><Label className="text-xs">{label}</Label><Input type={type} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} data-testid={testId} /></div>
);
const Row = ({ label, checked, onChange, disabled, testId }) => (
  <div className={`flex items-center justify-between border rounded-md p-3 ${disabled ? "opacity-60" : ""}`}><span className="text-sm">{label}</span><Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} /></div>
);
